/* DEV-292 (2026-09-26): the saved list ("♡ atranka") outside the product cards. The list itself is kept by assets/lm-card.js
   (localStorage "lm-atranka", product handles, newest first, event "lm:atranka"). Loaded on every page from sections/lm-category-bar.liquid.
   - header: a ♡ link to /pages/wishlist with the number of saved products (sections/header.liquid prints the link);
   - /pages/wishlist (sections/lm-atranka.liquid): the saved products as cards with the size sheet and "+";
   - the third save shows a short note with a link to the list;
   - cart drawer (sections/cart-drawer.liquid): "From your saved list" row with saved products not yet in the cart (up to 6);
   - design v11 (T4): every ♡ link to the list opens it as a sheet (bottom sheet on phones, 440 px side panel from 1000 px).
   Cards come from /products/<handle>?view=lm-card (templates/product.lm-card.liquid), so they are the same cards as in collections. */
const config = (() => {
  try { return JSON.parse(document.querySelector('[data-lm-atranka]').textContent); } catch (error) { return {}; }
})();

const TEXT = {
  en: { title: 'Your saved list', empty: 'Your list is empty. Tap ♡ on a product to save it.', browse: 'Browse New In', row: 'From your saved list', toast: '3 products saved.', view: 'View your list', all: 'Open saved list', close: 'Close' },
  lt: { title: 'Tavo atranka', empty: 'Atranka tuščia. Paspausk ♡ ant prekės, kad ją išsaugotum.', browse: 'Žiūrėti naujienas', row: 'Iš tavo atrankos', toast: 'Atrankoje jau 3 prekės.', view: 'Peržiūrėk atranką', all: 'Atidaryti atranką', close: 'Uždaryti' },
  de: { title: 'Ihre Merkliste', empty: 'Ihre Merkliste ist leer. Tippen Sie auf ♡, um einen Artikel zu speichern.', browse: 'Neuheiten ansehen', row: 'Aus Ihrer Merkliste', toast: '3 Artikel gemerkt.', view: 'Merkliste ansehen', all: 'Merkliste öffnen', close: 'Schließen' },
  da: { title: 'Din ønskeliste', empty: 'Din ønskeliste er tom. Tryk på ♡ på et produkt for at gemme det.', browse: 'Se nyheder', row: 'Fra din ønskeliste', toast: '3 produkter gemt.', view: 'Se din ønskeliste', all: 'Åbn ønskelisten', close: 'Luk' },
  fi: { title: 'Toivelistasi', empty: 'Toivelistasi on tyhjä. Tallenna tuote napauttamalla ♡.', browse: 'Katso uutuudet', row: 'Toivelistaltasi', toast: '3 tuotetta tallennettu.', view: 'Näytä toivelista', all: 'Avaa toivelista', close: 'Sulje' },
  fr: { title: 'Ma sélection', empty: 'Votre sélection est vide. Touchez ♡ sur un article pour l’enregistrer.', browse: 'Voir les nouveautés', row: 'De votre sélection', toast: '3 articles enregistrés.', view: 'Voir ma sélection', all: 'Ouvrir ma sélection', close: 'Fermer' },
  nl: { title: 'Je verlanglijst', empty: 'Je verlanglijst is leeg. Tik op ♡ bij een product om het te bewaren.', browse: 'Bekijk nieuw binnen', row: 'Uit je verlanglijst', toast: '3 producten bewaard.', view: 'Bekijk je verlanglijst', all: 'Open je verlanglijst', close: 'Sluiten' },
  no: { title: 'Din ønskeliste', empty: 'Ønskelisten din er tom. Trykk på ♡ på et produkt for å lagre det.', browse: 'Se nyheter', row: 'Fra ønskelisten din', toast: '3 produkter lagret.', view: 'Se ønskelisten', all: 'Åpne ønskelisten', close: 'Lukk' },
  sv: { title: 'Din önskelista', empty: 'Din önskelista är tom. Tryck på ♡ på en produkt för att spara den.', browse: 'Se nyheter', row: 'Från din önskelista', toast: '3 produkter sparade.', view: 'Visa önskelistan', all: 'Öppna önskelistan', close: 'Stäng' }
};
const lang = (document.documentElement.lang || 'en').toLowerCase().slice(0, 2);
const t = TEXT[lang === 'nb' ? 'no' : lang] || TEXT.en;
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const KEY = 'lm-atranka';

const readSaved = () => {
  try {
    const list = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(list) ? list.filter((item) => typeof item === 'string' && item) : [];
  } catch (error) {
    return [];
  }
};

/* the card script binds hearts, swipe and "+" on any card added to the page */
let cardScript = false;
const loadCardScript = () => {
  if (cardScript || !config.card) return;
  cardScript = true;
  import(config.card).catch(() => {});
};

/* one card per handle, fetched once per page; a removed or unpublished product gives null */
const cards = new Map();
const fetchCard = (handle) => {
  if (!cards.has(handle)) {
    cards.set(handle, fetch(root + 'products/' + encodeURIComponent(handle) + '?view=lm-card', { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const card = doc.querySelector('product-card');
        if (card) card.removeAttribute('reveal-js');
        return card ? card.outerHTML : null;
      })
      .catch(() => null));
  }
  return cards.get(handle);
};

/* AUD-1013-F08 (G, 2026-10-13): the same card can be on the page twice (the list and the cart drawer's "From your saved
   list" row), and its colour radios have fixed ids and one name. With two copies every label pointed at the first copy, so
   the list page radios had no name for screen readers, and both copies were one radio group (only one could be checked).
   Each copy now gets its own ids, label targets and radio names. */
let copies = 0;
const cardNode = (html) => {
  const box = document.createElement('div');
  box.innerHTML = html;
  const card = box.firstElementChild;
  if (card) {
    const suffix = '-lma' + (++copies);
    const ids = new Set();
    card.querySelectorAll('[id]').forEach((node) => { ids.add(node.id); node.id += suffix; });
    card.querySelectorAll('label[for]').forEach((node) => { if (ids.has(node.htmlFor)) node.htmlFor += suffix; });
    card.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls]').forEach((node) => {
      ['aria-labelledby', 'aria-describedby', 'aria-controls'].forEach((name) => {
        const value = node.getAttribute(name);
        if (value) node.setAttribute(name, value.split(/\s+/).map((id) => (ids.has(id) ? id + suffix : id)).join(' '));
      });
    });
    card.querySelectorAll('input[type="radio"][name]').forEach((node) => { node.name += suffix; });
  }
  return card;
};

/* ---------- header ♡ with the count ---------- */
const header = () => {
  const count = readSaved().length;
  document.querySelectorAll('[data-lm-hdr-heart]').forEach((link) => {
    const bubble = link.querySelector('[data-lm-hdr-count]');
    link.setAttribute('aria-label', t.title + (count ? ' (' + count + ')' : ''));
    if (bubble) {
      bubble.textContent = String(count);
      bubble.classList.toggle('opacity-0', count === 0);
    }
  });
};

/* ---------- the list page ---------- */
const page = document.querySelector('[data-lm-atranka-page]');
let pageRun = 0;
const renderPage = async () => {
  if (!page) return;
  const run = ++pageRun;
  page.querySelectorAll('[data-lm-t]').forEach((node) => { node.textContent = t[node.dataset.lmT] || ''; });
  const grid = page.querySelector('[data-lm-atranka-grid]');
  const empty = page.querySelector('[data-lm-atranka-empty]');
  const list = readSaved();
  const shown = [...grid.children].map((child) => child.getAttribute('handle'));
  /* a heart tapped on this page only takes that card away, so the page does not jump */
  if (shown.length && list.every((handle) => shown.includes(handle))) {
    [...grid.children].forEach((child) => { if (!list.includes(child.getAttribute('handle'))) child.remove(); });
    empty.hidden = grid.children.length > 0;
    return;
  }
  const html = await Promise.all(list.map(fetchCard));
  if (run !== pageRun) return;
  grid.replaceChildren(...html.filter(Boolean).map(cardNode));
  grid.style.minHeight = '';
  empty.hidden = grid.children.length > 0;
  if (grid.children.length) loadCardScript();
};

/* ---------- a short note on the third save ---------- */
let lastCount = readSaved().length;
const toast = (count) => {
  const grew = count > lastCount;
  const third = grew && lastCount < 3 && count >= 3;
  lastCount = count;
  if (!third || page || !config.page) return;
  let note = document.querySelector('.lm-toast');
  if (!note) {
    note = document.createElement('div');
    note.className = 'lm-toast';
    note.setAttribute('role', 'status');
    note.innerHTML = '<span></span> <a></a>';
    document.body.append(note);
  }
  note.querySelector('span').textContent = t.toast;
  const link = note.querySelector('a');
  link.href = config.page;
  link.textContent = t.view;
  note.hidden = false;
  clearTimeout(note.__timer);
  note.__timer = setTimeout(() => { note.hidden = true; }, 5000);
};

/* ---------- cart drawer row ---------- */
const fillCartRow = async (row) => {
  if (row.dataset.lmFilled) return;
  row.dataset.lmFilled = 'true';
  const inCart = new Set((row.dataset.inCart || '').split(',').filter(Boolean));
  const list = readSaved().filter((handle) => !inCart.has(handle)).slice(0, 6);
  if (!list.length) { row.hidden = true; return; }
  const html = (await Promise.all(list.map(fetchCard))).filter(Boolean);
  if (!html.length || !row.isConnected) return;
  row.innerHTML = '<p class="lm-cart-saved__title"></p><div class="lm-cart-saved__row"></div>';
  row.querySelector('.lm-cart-saved__title').textContent = t.row;
  row.querySelector('.lm-cart-saved__row').append(...html.map(cardNode));
  row.hidden = false;
  loadCardScript();
};
const cartRows = () => document.querySelectorAll('[data-lm-cart-saved]').forEach(fillCartRow);

/* the theme redraws the drawer after every cart change; the new row fills itself */
new MutationObserver(() => {
  if (document.querySelector('[data-lm-cart-saved]:not([data-lm-filled])')) cartRows();
}).observe(document.body, { childList: true, subtree: true });

/* ---------- the saved list as a sheet (design v11, T4) ----------
   Every ♡ link to the list page (header, bottom bars, the third-save note) opens "♡ My saved list (N)": a bottom sheet on
   phones and a 440 px side panel from 1000 px. Each saved product is one row: photo, name, price, its sizes, "Add to cart"
   (enabled once a size is picked), "Remove" and "Open". Rows are built from /products/<handle>.js; the list page
   /pages/wishlist stays for a direct link. */
const SHEET = {
  lt: { title: '♡ Mano atranka (%N)', empty: 'Atranka tuščia.', emptyHow: 'Spausk ♡ ant bet kurios prekės, kad ją išsaugotum. Prisijungti nereikia.', add: 'Į krepšelį', remove: 'Pašalinti', open: 'Atidaryti', fine: 'Atranka saugoma šioje naršyklėje.', fail: 'Nepavyko įdėti į krepšelį.' },
  de: { title: '♡ Meine Merkliste (%N)', empty: 'Ihre Merkliste ist leer.', emptyHow: 'Tippen Sie bei einem beliebigen Artikel auf ♡, um ihn zu speichern. Eine Anmeldung ist nicht nötig.', add: 'In den Warenkorb', remove: 'Entfernen', open: 'Öffnen', fine: 'Die Merkliste wird in diesem Browser gespeichert.', fail: 'Konnte nicht in den Warenkorb gelegt werden.' },
  da: { title: '♡ Min ønskeliste (%N)', empty: 'Ønskelisten er tom.', emptyHow: 'Tryk på ♡ på et produkt for at gemme det. Du behøver ikke logge ind.', add: 'Læg i kurv', remove: 'Fjern', open: 'Åbn', fine: 'Ønskelisten gemmes i denne browser.', fail: 'Kunne ikke lægges i kurven.' },
  fi: { title: '♡ Toivelistani (%N)', empty: 'Toivelista on tyhjä.', emptyHow: 'Tallenna tuote napauttamalla ♡. Kirjautumista ei tarvita.', add: 'Lisää ostoskoriin', remove: 'Poista', open: 'Avaa', fine: 'Toivelista tallennetaan tähän selaimeen.', fail: 'Lisääminen ostoskoriin epäonnistui.' },
  fr: { title: '♡ Ma sélection (%N)', empty: 'Votre sélection est vide.', emptyHow: 'Touchez ♡ sur un article pour l’enregistrer. Aucune connexion nécessaire.', add: 'Ajouter au panier', remove: 'Retirer', open: 'Ouvrir', fine: 'Votre sélection est enregistrée dans ce navigateur.', fail: 'Impossible d’ajouter au panier.' },
  nl: { title: '♡ Mijn verlanglijst (%N)', empty: 'Je verlanglijst is leeg.', emptyHow: 'Tik op ♡ bij een product om het te bewaren. Inloggen is niet nodig.', add: 'In winkelwagen', remove: 'Verwijderen', open: 'Openen', fine: 'Je verlanglijst wordt in deze browser bewaard.', fail: 'Toevoegen aan winkelwagen is mislukt.' },
  no: { title: '♡ Min ønskeliste (%N)', empty: 'Ønskelisten er tom.', emptyHow: 'Trykk på ♡ på et produkt for å lagre det. Du trenger ikke logge inn.', add: 'Legg i handlekurv', remove: 'Fjern', open: 'Åpne', fine: 'Ønskelisten lagres i denne nettleseren.', fail: 'Kunne ikke legges i handlekurven.' },
  sv: { title: '♡ Min önskelista (%N)', empty: 'Önskelistan är tom.', emptyHow: 'Tryck på ♡ på en produkt för att spara den. Du behöver inte logga in.', add: 'Lägg i varukorgen', remove: 'Ta bort', open: 'Öppna', fine: 'Önskelistan sparas i den här webbläsaren.', fail: 'Kunde inte läggas i varukorgen.' },
  en: { title: '♡ My saved list (%N)', empty: 'Your saved list is empty.', emptyHow: 'Tap ♡ on any product to save it. No account needed.', add: 'Add to cart', remove: 'Remove', open: 'Open', fine: 'Your list is kept in this browser.', fail: 'Could not add to cart.' }
};
const st = SHEET[lang === 'nb' ? 'no' : lang] || SHEET.en;
const SIZE_RE = /size|dyd|größe|grösse|taille|maat|storlek|størrelse|koko/i;
const COLOUR_RE = /colou?r|spalv|farbe|couleur|kleur|färg|farve|farge|väri/i;
const esc = (value) => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const money = (cents) => {
  const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
  const whole = cents % 100 === 0;
  if (cur === 'EUR') return '€' + (whole ? String(cents / 100) : (cents / 100).toFixed(2).replace('.', ','));
  try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cur, minimumFractionDigits: whole ? 0 : 2 }).format(cents / 100); } catch (error) { return (cents / 100).toFixed(2) + ' ' + cur; }
};
const products = new Map();
const fetchProduct = (handle) => {
  if (!products.has(handle)) {
    products.set(handle, fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null));
  }
  return products.get(handle);
};
let panel = null;
let panelRun = 0;
const picked = {};
const rowHtml = (p) => {
  const match = String(p.title).match(/^(.+?)\s+[–—-]\s+(.+)$/);
  const name = match ? match[1] : p.title;
  const sub = match ? match[2] : '';
  const sizeIndex = (p.options || []).findIndex((option) => SIZE_RE.test(option.name || option));
  const sizes = sizeIndex < 0 ? [] : (p.options[sizeIndex].values || []).filter((value) => p.variants.some((v) => v.available && v.options[sizeIndex] === value));
  const img = p.featured_image ? p.featured_image + (p.featured_image.includes('?') ? '&' : '?') + 'width=160' : '';
  const sale = p.compare_at_price && p.compare_at_price > p.price;
  const ready = sizeIndex < 0 || picked[p.handle];
  return '<div class="lm-sv" data-h="' + esc(p.handle) + '">' + (img ? '<img src="' + esc(img) + '" alt="' + esc(name) + '" width="72" height="92" loading="lazy">' : '<span class="lm-sv__ph"></span>') +
    '<div class="lm-sv__r"><div class="lm-sv__nm">' + esc(name) + '</div>' + (sub ? '<div class="lm-sv__sb">' + esc(sub) + '</div>' : '') +
    '<div class="lm-sv__pr' + (sale ? ' is-sale' : '') + '">' + money(p.price) + (sale ? ' <s>' + money(p.compare_at_price) + '</s>' : '') + '</div>' +
    (sizes.length ? '<div class="lm-sv__s">' + sizes.map((size) => '<button type="button" data-ss="' + esc(size) + '"' + (picked[p.handle] === size ? ' class="on" aria-pressed="true"' : ' aria-pressed="false"') + '>' + esc(size) + '</button>').join('') + '</div>' : '') +
    '<div class="lm-sv__row"><button type="button" class="lm-sv__add" data-sadd' + (ready ? '' : ' disabled') + '>' + esc(st.add) + '</button><button type="button" class="lm-sv__rm" data-srm>' + esc(st.remove) + '</button><a class="lm-sv__rm" href="' + esc(root + 'products/' + p.handle) + '">' + esc(st.open) + '</a></div>' +
    '<p class="lm-sv__err" hidden></p></div></div>';
};
const renderPanel = async () => {
  if (!panel || panel.hidden) return;
  const run = ++panelRun;
  const list = readSaved();
  const body = panel.querySelector('.lm-apanel__b');
  const found = (await Promise.all(list.map(fetchProduct))).filter(Boolean);
  if (run !== panelRun) return;
  panel.querySelector('h2').textContent = st.title.replace('%N', found.length);
  /* DEV-445 (2026-09-28): the empty list gave nothing to tap (Clarity: 16 dead clicks on "Your saved list is empty"),
     so it now ends with a link to New In in the page language */
  body.innerHTML = found.length ? found.map(rowHtml).join('') : '<p class="lm-apanel__empty">' + esc(st.empty) + '<br>' + esc(st.emptyHow) + '<br><a href="' + esc(root + 'collections/new-in') + '" style="display:inline-block;min-height:44px;padding:11px 8px;box-sizing:border-box">' + esc(t.browse) + ' →</a></p>';
};
const closePanel = () => {
  if (!panel || panel.hidden) return;
  panel.classList.remove('show');
  panel.__scrim.classList.remove('show');
  setTimeout(() => { panel.hidden = true; panel.__scrim.hidden = true; }, 250);
  document.documentElement.style.overflow = '';
  if (panel.__opener && panel.__opener.focus) panel.__opener.focus();
};
const addToCart = async (row, handle) => {
  const p = await fetchProduct(handle);
  if (!p) return;
  const sizeIndex = (p.options || []).findIndex((option) => SIZE_RE.test(option.name || option));
  const variant = p.variants.find((v) => v.available && (sizeIndex < 0 || v.options[sizeIndex] === picked[handle]));
  const error = row.querySelector('.lm-sv__err');
  const button = row.querySelector('[data-sadd]');
  if (!variant) return;
  button.disabled = true;
  try {
    /* CRO-48 (G, 2026-10-15): the cart drawer reads the new drawer HTML from cart.sections; without it the theme's drawer threw
       "Cannot read properties of undefined" after every add from the saved list. Same request as the card quick add (lm-card.js). */
    const sections = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    const response = await fetch(root + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ id: variant.id, quantity: 1, sections: sections.join(','), sections_url: root + 'variants/' + variant.id }) });
    if (!response.ok) throw new Error(response.status);
    const json = await response.json();
    const cart = await (await fetch(root + 'cart.js', { credentials: 'same-origin' })).json();
    cart.sections = json.sections;
    if (cart.sections) document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    closePanel();
    const drawer = document.getElementById('cart-drawer');
    if (drawer && typeof drawer.show === 'function') setTimeout(() => drawer.show(), 260);
  } catch (e) {
    error.textContent = st.fail;
    error.hidden = false;
  } finally {
    button.disabled = false;
  }
};
const openPanel = (opener, byKey) => {
  if (!panel) {
    const scrim = document.createElement('div');
    scrim.className = 'lm-apanel-scrim';
    scrim.hidden = true;
    panel = document.createElement('aside');
    panel.className = 'lm-apanel';
    panel.hidden = true;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', t.title);
    panel.innerHTML = '<div class="lm-apanel__h"><h2></h2><button type="button" class="lm-apanel__x">×</button></div><div class="lm-apanel__b"></div><div class="lm-apanel__f"></div>';
    panel.querySelector('.lm-apanel__f').textContent = st.fine;
    const x = panel.querySelector('.lm-apanel__x');
    x.setAttribute('aria-label', t.close);
    panel.__scrim = scrim;
    document.body.append(scrim, panel);
    scrim.addEventListener('click', closePanel);
    x.addEventListener('click', closePanel);
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closePanel(); });
    panel.addEventListener('click', (event) => {
      const row = event.target.closest('.lm-sv');
      if (!row) return;
      const handle = row.dataset.h;
      const size = event.target.closest('[data-ss]');
      if (size) {
        picked[handle] = size.dataset.ss;
        row.querySelectorAll('[data-ss]').forEach((b) => { const on = b === size; b.classList.toggle('on', on); b.setAttribute('aria-pressed', String(on)); });
        row.querySelector('[data-sadd]').disabled = false;
        return;
      }
      if (event.target.closest('[data-sadd]')) { addToCart(row, handle); return; }
      if (event.target.closest('[data-srm]')) {
        const list = readSaved().filter((item) => item !== handle);
        try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (error) {}
        document.querySelectorAll('[data-lm-heart="' + CSS.escape(handle) + '"]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
        document.dispatchEvent(new CustomEvent('lm:atranka', { detail: { items: list } }));
      }
    });
  }
  panel.__opener = opener;
  panel.querySelector('h2').textContent = st.title.replace('%N', readSaved().length);
  panel.hidden = false;
  panel.__scrim.hidden = false;
  document.documentElement.style.overflow = 'hidden';
  requestAnimationFrame(() => { panel.classList.add('show'); panel.__scrim.classList.add('show'); });
  /* a mouse or touch open gets no focus ring on the close button (v11); keyboard users keep it */
  panel.querySelector('.lm-apanel__x').focus({ focusVisible: !!byKey });
  renderPanel();
};
const isListLink = (link) => {
  if (link.hasAttribute('data-lm-hdr-heart')) return true;
  if (!config.page) return false;
  try { return new URL(link.href, location.href).pathname.replace(/\/$/, '') === new URL(config.page, location.href).pathname.replace(/\/$/, ''); } catch (error) { return false; }
};
document.addEventListener('click', (event) => {
  const link = event.target.closest && event.target.closest('a[href], [data-lm-hdr-heart]');
  if (!link || page || event.metaKey || event.ctrlKey || event.shiftKey || !isListLink(link)) return;
  event.preventDefault();
  openPanel(link, event.detail === 0);
});

document.addEventListener('lm:atranka', (event) => {
  const count = (event.detail && Array.isArray(event.detail.items) ? event.detail.items : readSaved()).length;
  header();
  toast(count);
  renderPage();
  renderPanel();
});
window.addEventListener('storage', (event) => {
  if (event.key !== KEY) return;
  lastCount = readSaved().length;
  header();
  renderPage();
});

header();
renderPage();
cartRows();
